import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, X, Target as TargetIcon, Calendar } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";

function fmtMonthLabel(m) {
  if (!m) return "";
  const [y, mm] = m.split("-");
  try { return new Date(Number(y), Number(mm) - 1, 1).toLocaleString("default", { month: "short", year: "numeric" }); }
  catch { return m; }
}

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const METRIC_LABELS = {
  accounts: "Accounts (Live in month)",
  shipments: "Shipments",
  revenue: "Revenue",
};

const emptyForm = () => ({
  agent_id: "", month: thisMonth(), metric_type: "accounts",
  target_value: "", client_type: "", notes: "",
});

export default function AdminTargets() {
  const [opts, setOpts] = useState({ agents: [], client_types: [], metric_types: [], months: [] });
  const [filterMonth, setFilterMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [optsRes, rowsRes] = await Promise.all([
        api.get("/targets/filter-options"),
        api.get(`/targets?month=${filterMonth}`),
      ]);
      setOpts(optsRes.data);
      setRows(rowsRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load targets");
    }
  };

  useEffect(() => { load(); }, [filterMonth]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agent_id) return toast.error("Pick an agent");
    if (!form.target_value || Number(form.target_value) < 0) return toast.error("Enter a target value");
    setSaving(true);
    try {
      await api.post("/targets", {
        ...form,
        target_value: Number(form.target_value),
        client_type: form.client_type || null,
        notes: form.notes || null,
      });
      toast.success("Target saved");
      setForm({ ...emptyForm(), month: filterMonth });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this target?")) return;
    try {
      await api.delete(`/targets/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Layout title="Targets" breadcrumb={["Admin", "Targets"]}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* New target form */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2 mb-3">
            <TargetIcon size={16} className="text-orange-600" /> New target
          </h3>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Agent">
              <select required value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })} data-testid="target-agent-select" className="ld-input">
                <option value="">— Select agent —</option>
                {opts.agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Month">
                <input required type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="target-month-input" className="ld-input" />
              </Field>
              <Field label="Metric">
                <select value={form.metric_type} onChange={(e) => setForm({ ...form, metric_type: e.target.value })} data-testid="target-metric-select" className="ld-input">
                  {(opts.metric_types || ["accounts", "shipments", "revenue"]).map((m) => (
                    <option key={m} value={m}>{METRIC_LABELS[m] || m}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Target value">
              <input required type="number" min="0" step="0.01" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} data-testid="target-value-input" placeholder="e.g. 50000" className="ld-input" />
            </Field>
            <Field label="Client type (optional)">
              <select value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} data-testid="target-client-type-select" className="ld-input">
                <option value="">All client types</option>
                {opts.client_types.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Notes (optional)">
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ld-input" />
            </Field>
            <button data-testid="target-submit" disabled={saving} className="w-full text-sm font-medium px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white inline-flex items-center justify-center gap-1.5">
              <Save size={13} /> {saving ? "Saving…" : "Save target"}
            </button>
          </form>
        </div>

        {/* Existing targets list */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-900">Targets for the selected month</h3>
            <div className="inline-flex items-center gap-2">
              <Calendar size={14} className="text-slate-400" />
              <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} data-testid="filter-month-select" className="text-sm px-2 py-1.5 border border-slate-200 rounded-md bg-white">
                {(opts.months || []).map((m) => <option key={m} value={m}>{fmtMonthLabel(m)}</option>)}
              </select>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Agent</th>
                <th className="text-left px-5 py-3">Metric</th>
                <th className="text-right px-5 py-3">Target</th>
                <th className="text-left px-5 py-3">Client type</th>
                <th className="text-left px-5 py-3">Notes</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="targets-list">
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-500">No targets set for {fmtMonthLabel(filterMonth)} yet.</td></tr>
              )}
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium">{t.agent_name}</td>
                  <td className="px-5 py-3 text-slate-700">{METRIC_LABELS[t.metric_type] || t.metric_type}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">{Number(t.target_value).toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-700">{t.client_type || <span className="text-slate-400">All</span>}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{t.notes}</td>
                  <td className="px-5 py-3 text-right">
                    <button data-testid={`target-delete-${t.id}`} onClick={() => remove(t.id)} className="text-slate-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
